import styles from './scenarioMainHeader.module.scss';
import {
  useDispatch,
  useSelector
} from "react-redux";
import { useTranslation } from "react-i18next";
import {
  MouseEvent,
  useEffect,
  useState
} from "react";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { useGetAllTestCaseTemplateQuery } from "../../../api/testCaseTemplateService";
import { Accordion } from "../../../shared/components/accordion/accordion";
import arrowIcon from "../../../shared/images/arrow.svg";
import closeIcon from "../../../shared/images/close.svg";
import {
  deleteSelectedTestCaseTemplate,
  setSelectedTestCase
} from "../../../store/slices/testCaseTemplateSlice";
import { deleteScenarioExecuteResponse } from "../../../store/slices/scenarioSlice";
import { getTemplateAndTestCaseByTestCaseId } from "../../../utils/testCaseUtils";
import { ScenarioTestCase } from "../../../types/scenarioTypes";
import { PassedAndFailedResultFilter } from "../../../components/passedAndFailedResultFilter/passedAndFailedResultFilter";
import { StatusCodeFilterResult } from "../../../components/statusCodeFilterResult/statusCodeFilterResult";
import { TotalExecutionTime } from "../../../components/totalExecutionTime/totalExecutionTime";
import { ScenarioMainHeaderItem } from "./scenarioMainHeaderItem/scenarioMainHeaderItem";
import {
  ResultEnum,
  TransformExecuteResponse
} from "../../../types/clientTypes";
import { selectScenarioExecuteResponses } from "../../../store/selectors";
import { StatusCode } from "../../../types/sharedTypes";
import { SearchInput } from "../../../shared/components/searchInput/searchInput";

export const ScenarioMainHeader = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation();

  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);

  const selectedScenario = useSelector((state: RootState) => state.scenarioSlice.selectedScenario);
  const selectedTestCases = useSelector((state: RootState) => state.scenarioSlice.selectedTestCases);
  const scenarioExecuteResponses = useSelector(selectScenarioExecuteResponses);

  const [searchValue, setSearchValue] = useState<string>("");
  const [needResetFilter, setNeedResetFilter] = useState(true);

  const [localSelectedTestCases, setLocalSelectedTestCases] = useState<Array<ScenarioTestCase>>([]);
  const [resultFilter, setResultFilter] = useState<ResultEnum>();
  const [statusCodeFilter, setStatusCodeFilter] = useState<StatusCode>();
  const [filteredSelectedTestCases, setFilteredSelectedTestCases] = useState<Array<ScenarioTestCase>>([]);
  const [filteredExecuteResponses, setFilteredExecuteResponses] = useState<Array<TransformExecuteResponse>>([]);

  const { data: testCaseTemplates } = useGetAllTestCaseTemplateQuery("");


  useEffect(() => {
    setLocalSelectedTestCases(selectedTestCases.filter(item => !item.isDeleted));
    if (needResetFilter) {
      setResultFilter(undefined);
      setStatusCodeFilter(undefined);
    } else {
      setNeedResetFilter(true);
    }
  }, [selectedTestCases, setLocalSelectedTestCases, setNeedResetFilter]);

  useEffect(() => {
    let newFilteredTestCases = localSelectedTestCases.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase()));
    if (!!resultFilter) {
      const resultsIds = scenarioExecuteResponses
        .filter(item => item.result === resultFilter || !!item.isLoad)
        .map(item => item.internalId);
      newFilteredTestCases = newFilteredTestCases
        .filter(item => resultsIds.includes(item.internalId));
    }
    if (!!statusCodeFilter) {
      const resultsIds = scenarioExecuteResponses
        .filter(item => item.statusCode === statusCodeFilter.code || !!item.isLoad)
        .map(item => item.internalId);
      newFilteredTestCases = newFilteredTestCases
        .filter(item => resultsIds.includes(item.internalId));
    }
    const internalIds = newFilteredTestCases.map(item => item.internalId);
    setFilteredExecuteResponses(scenarioExecuteResponses.filter(item => internalIds.includes(item.internalId)));
    setFilteredSelectedTestCases(newFilteredTestCases);
  }, [searchValue, resultFilter, scenarioExecuteResponses, statusCodeFilter, localSelectedTestCases, setFilteredExecuteResponses, setFilteredSelectedTestCases]);

  useEffect(() => {
    const findTestCase = filteredSelectedTestCases.find(item => item.internalId === selectedTestCase.internalId);
    if (findTestCase) {
      const template = testCaseTemplates?.find(item => item.id === selectedTestCase.testCaseTemplateId);
      if (!!template) {
        dispatch(setSelectedTestCase({ testCase: findTestCase, template }));
      } else {
        console.error("Error find template is scenarios");
      }
    } else {
      const testCase = filteredSelectedTestCases[0];
      if (!!testCase && !!testCaseTemplates) {
        const testCaseAndTemplate = getTemplateAndTestCaseByTestCaseId(testCaseTemplates, testCase.id as number);
        const template = testCaseAndTemplate?.template;
        if (!!template) {
          dispatch(setSelectedTestCase({ testCase, template }));
        }
      } else {
        dispatch(deleteSelectedTestCaseTemplate());
      }
    }
  }, [filteredSelectedTestCases, dispatch]);

  const clickClearAll = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    dispatch(deleteScenarioExecuteResponse())
  }

  return (
    <div className={styles.container}>
      <Accordion activeClassName={styles.active}
                 maxHeight={400}
                 activeIndex={0}
                 disabledAutoScroll
                 data={
                   [{
                     title: (
                       <div className={styles.accordionTitle}>
                         <div className={styles.main}>
                           <img src={arrowIcon} alt=""
                                className={styles.arrow}/>
                           {t("common.results")}
                           <div className={styles.search} onClick={(e) => e.stopPropagation()}>
                             <SearchInput value={searchValue} onChange={setSearchValue}/>
                           </div>
                         </div>
                         <div className={styles.status}>
                           { scenarioExecuteResponses.length !== 0 && (
                               <StatusCodeFilterResult onChangeFilter={setStatusCodeFilter}
                                                       filter={statusCodeFilter}
                                                       executeResponses={filteredExecuteResponses}/>
                           )}

                         </div>
                         <div className={styles.result}>
                           { scenarioExecuteResponses.length !== 0 && (
                               <PassedAndFailedResultFilter onChangeFilter={setResultFilter}
                                                            filter={resultFilter}
                                                            executeResponses={filteredExecuteResponses}/>
                           )}
                         </div>
                         <div>
                           { scenarioExecuteResponses.length !== 0 && (
                               <TotalExecutionTime executeResponses={filteredExecuteResponses}
                                                   maximumExecutionTimeout={selectedScenario.maximumExecutionTimeout}/>
                           )}
                         </div>
                         <div className={styles.clearBlock} onClick={clickClearAll}>
                           <img src={closeIcon} alt=""/>
                           {t("common.clearAll")}
                         </div>
                       </div>
                     ),
                     child: (
                       <div className={styles.items}>
                         { !!filteredSelectedTestCases.length
                             ? filteredSelectedTestCases.map(((testCase, index) => (
                                 <ScenarioMainHeaderItem key={index}
                                                         onClickRerun={() => setNeedResetFilter(false)}
                                                         testCaseTemplates={testCaseTemplates || []}
                                                         testCase={testCase}/>
                             )))
                             : <div className={styles.noResults}>{t("common.noResult")}</div>
                         }
                       </div>
                     )
                   }]
                 }/>
    </div>
  )
}
